import React, { useMemo, useState } from 'react';
import {
  BrioModal, BrioModalBody, BrioModalField, BrioButton, BrioInput, BrioSelect,
} from '../../components/BrioComponents';

// ============================================================================
// Add a custom (manual) library rate against one item, from the TD rate
// library when it was reached from that item's rate link. Saved through
// boq.add_custom_rate with source = 'manual' and no source file.
//
// Consultant and Bid by are free text: a custom rate usually comes from a
// quote or a conversation that has no source document behind it. Consultant is
// required, here and in the RPC, because with no source file it is the only
// record of where the figure came from.
// ============================================================================

const today = () => new Date().toISOString().slice(0, 10);

function AddCustomRateModal({ itemCode, versions, vendors, showSplits, onSave, onClose }) {
  const choices = useMemo(
    () => (versions || []).filter((v) => v.status !== 'deprecated')
      .sort((a, b) => (a.fullCode || '').localeCompare(b.fullCode || '')),
    [versions],
  );
  const [form, setForm] = useState({
    itemVersionId: choices.length === 1 ? choices[0].id : '',
    basis: 'estimate',
    rate: '',
    supply: '',
    install: '',
    rateDate: today(),
    consultant: '',
    contractor: '',
    vendorId: '',
    city: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const chosen = choices.find((v) => v.id === form.itemVersionId);
  const splitTotal = form.supply !== '' || form.install !== ''
    ? Number(form.supply || 0) + Number(form.install || 0) : null;

  const save = async () => {
    setError(null);
    if (!form.itemVersionId) { setError('Choose which version of the item this rate is for.'); return; }
    // The consultant is the only record of where a custom figure came from.
    if (!form.consultant.trim()) { setError('Enter the consultant this rate came from.'); return; }
    const rate = splitTotal != null ? splitTotal : form.rate;
    if (rate === '' || rate == null || !Number.isFinite(Number(rate)) || Number(rate) < 0) {
      setError('Enter a rate of zero or more.');
      return;
    }
    setSaving(true);
    const res = await onSave({ ...form, rate });
    setSaving(false);
    if (!res.success) { setError(res.error || 'Could not save the rate.'); return; }
    onClose();
  };

  return (
    <BrioModal
      title={`Add custom rate · ${itemCode}`}
      onClose={onClose}
      width="md"
      footer={(
        <>
          <BrioButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</BrioButton>
          <BrioButton variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add rate'}</BrioButton>
        </>
      )}
    >
      <BrioModalBody form>
        <BrioModalField label="Item version">
          {choices.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: '#a94442' }}>This item has no live version to rate.</div>
          ) : (
            <BrioSelect value={form.itemVersionId} onChange={set('itemVersionId')} style={{ width: '100%' }}>
              {choices.length > 1 && <option value="">Choose a version…</option>}
              {choices.map((v) => (
                <option key={v.id} value={v.id}>{v.fullCode} · {v.shortDescription}</option>
              ))}
            </BrioSelect>
          )}
          {chosen && <div style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)', marginTop: 4 }}>Unit: {chosen.unit || '—'}</div>}
        </BrioModalField>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <BrioModalField label="Basis">
            <BrioSelect value={form.basis} onChange={set('basis')} style={{ width: 150 }}>
              <option value="estimate">Estimate</option>
              <option value="tender">Tender</option>
            </BrioSelect>
          </BrioModalField>
          <BrioModalField label={showSplits ? 'Rate (or enter the split)' : 'Rate'}>
            <BrioInput type="number" min="0" step="any" value={splitTotal != null ? String(splitTotal) : form.rate}
              onChange={set('rate')} disabled={splitTotal != null} style={{ width: 140, textAlign: 'right' }} />
          </BrioModalField>
          {showSplits && (
            <>
              <BrioModalField label="Supply">
                <BrioInput type="number" min="0" step="any" value={form.supply} onChange={set('supply')} style={{ width: 110, textAlign: 'right' }} />
              </BrioModalField>
              <BrioModalField label="Install">
                <BrioInput type="number" min="0" step="any" value={form.install} onChange={set('install')} style={{ width: 110, textAlign: 'right' }} />
              </BrioModalField>
            </>
          )}
          <BrioModalField label="Date">
            <BrioInput type="date" value={form.rateDate} onChange={set('rateDate')} style={{ width: 160 }} />
          </BrioModalField>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <BrioModalField label="Consultant *">
            <BrioInput value={form.consultant} onChange={set('consultant')} style={{ width: 230 }} />
          </BrioModalField>
          <BrioModalField label="Bid by">
            <BrioInput value={form.contractor} onChange={set('contractor')} style={{ width: 230 }} />
          </BrioModalField>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <BrioModalField label="Vendor (optional)">
            <BrioSelect value={form.vendorId} onChange={set('vendorId')} style={{ width: 230 }}>
              <option value="">None</option>
              {(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField label="City (optional)">
            <BrioInput value={form.city} onChange={set('city')} style={{ width: 230 }} />
          </BrioModalField>
        </div>

        {error && <div style={{ color: '#a94442', fontSize: '0.85rem' }}>{error}</div>}
      </BrioModalBody>
    </BrioModal>
  );
}

export default AddCustomRateModal;
